import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface Tenant {
  id: number;
  name: string;
  subdomain: string;
  status: string;
  subscription_ends_at: string | null;
  created_at: string;
}

const SuperadminTenants: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Create Tenant State
  const [isCreating, setIsCreating] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: '', subdomain: '', admin_email: '', admin_password: '' });
  
  // Edit Tenant State
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [editStatus, setEditStatus] = useState('active');
  const [editSubAdd, setEditSubAdd] = useState('');
  const [editName, setEditName] = useState('');
  const [editSubdomain, setEditSubdomain] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const res = await axios.get('http://localhost:8000/api/superadmin/tenants');
      setTenants(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await axios.post('http://localhost:8000/api/superadmin/tenants', newTenant);
      alert('School Tenant provisioned successfully!');
      setNewTenant({ name: '', subdomain: '', admin_email: '', admin_password: '' });
      fetchTenants();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error creating tenant');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTenant) return;
    setIsUpdating(true);
    
    try {
      const payload: any = { 
        status: editStatus,
        name: editName,
        subdomain: editSubdomain
      };
      if (editSubAdd) payload.subscription_add = editSubAdd;
      
      await axios.put(`http://localhost:8000/api/superadmin/tenants/${editingTenant.id}`, payload);
      alert('Tenant updated successfully!');
      setEditingTenant(null);
      fetchTenants();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error updating tenant');
    } finally {
      setIsUpdating(false);
    }
  };

  const openEditModal = (t: Tenant) => {
    setEditingTenant(t);
    setEditStatus(t.status);
    setEditName(t.name);
    setEditSubdomain(t.subdomain);
    setEditSubAdd(''); // reset subscription extension selection
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Manage Tenants</h1>
          <p className="text-sm text-slate-500 mt-1">Provision schools and manage subscriptions.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-8">
        
        {/* Tenants Data Table */}
        <div className="bg-white border border-slate-200 rounded-[10px] shadow-sm flex flex-col">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800">Tenant Directory</h2>
            <span className="bg-indigo-50 text-indigo-600 text-xs font-bold px-3 py-1 rounded-full">{tenants.length} Schools</span>
          </div>
          
          <div className="flex-1 overflow-x-auto">
            {isLoading ? (
              <div className="p-8 text-center text-slate-500 text-sm">Loading directory...</div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="py-3 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">School Details</th>
                    <th className="py-3 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Subscription</th>
                    <th className="py-3 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="py-3 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tenants.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-4 px-6">
                        <div className="font-bold text-sm text-slate-800">{t.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{t.subdomain}.system.edu</div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm font-semibold text-slate-700">
                          {t.subscription_ends_at ? new Date(t.subscription_ends_at).toLocaleDateString() : 'N/A'}
                        </div>
                        {t.subscription_ends_at && new Date(t.subscription_ends_at) < new Date() && (
                          <div className="text-[10px] font-bold text-rose-500 uppercase mt-1">Expired</div>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-2.5 py-1 text-[10px] uppercase font-bold rounded-md inline-block ${
                          t.status === 'active' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 
                          t.status === 'trial' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                          'bg-rose-50 text-rose-600 border border-rose-100'
                        }`}>
                          {t.status}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <button onClick={() => openEditModal(t)} className="px-3 py-1.5 text-xs font-bold bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 transition-colors">
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                  {tenants.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-12 text-center text-slate-500 text-sm">No schools provisioned yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Creation Panel */}
        <div className="bg-white border border-slate-200 rounded-[10px] shadow-sm p-6 h-max sticky top-8">
          <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4">
            <h2 className="text-lg font-bold text-slate-800">Add New Tenant</h2>
          </div>
          
          <form onSubmit={handleCreateTenant} className="space-y-4">
            <div>
              <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">School Name</label>
              <input type="text" value={newTenant.name} onChange={e => setNewTenant({...newTenant, name: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" required placeholder="e.g. Hillcrest High" />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">Subdomain</label>
              <div className="relative">
                <input type="text" value={newTenant.subdomain} onChange={e => setNewTenant({...newTenant, subdomain: e.target.value.toLowerCase()})} className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" required placeholder="hillcrest" />
              </div>
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">Admin Email</label>
              <input type="email" value={newTenant.admin_email} onChange={e => setNewTenant({...newTenant, admin_email: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" required placeholder="head@hillcrest.edu" />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">Initial Password</label>
              <input type="text" value={newTenant.admin_password} onChange={e => setNewTenant({...newTenant, admin_password: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" required placeholder="securePass123" />
            </div>
            
            <button type="submit" className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm py-2.5 px-4 rounded-md transition-all" disabled={isCreating}>
              {isCreating ? 'Provisioning...' : 'Provision Workspace'}
            </button>
          </form>
        </div>

      </div>

      {/* Edit Modal Overlay */}
      {editingTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-[10px] w-full max-w-md shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-800">Manage Tenant: {editingTenant.name}</h3>
              <button onClick={() => setEditingTenant(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            
            <form onSubmit={handleUpdateTenant} className="p-6 space-y-4">
              <div>
                <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">School Name</label>
                <input 
                  type="text" 
                  value={editName} 
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">Subdomain</label>
                <input 
                  type="text" 
                  value={editSubdomain} 
                  onChange={(e) => setEditSubdomain(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">Status</label>
                <select 
                  value={editStatus} 
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none"
                >
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">Extend Subscription</label>
                <select 
                  value={editSubAdd} 
                  onChange={(e) => setEditSubAdd(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none"
                >
                  <option value="">-- No Extension --</option>
                  <option value="1_month">Add 1 Month</option>
                  <option value="2_months">Add 2 Months</option>
                  <option value="1_term">Add 1 Term (~4 Months)</option>
                  <option value="2_terms">Add 2 Terms (~8 Months)</option>
                  <option value="1_year">Add 1 Year</option>
                </select>
                <p className="text-[11px] text-slate-500 mt-1">Current expiry: {editingTenant.subscription_ends_at ? new Date(editingTenant.subscription_ends_at).toLocaleDateString() : 'N/A'}</p>
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setEditingTenant(null)} className="flex-1 px-4 py-2 bg-slate-100 text-slate-600 font-bold text-sm rounded hover:bg-slate-200">Cancel</button>
                <button type="submit" disabled={isUpdating} className="flex-1 px-4 py-2 bg-indigo-600 text-white font-bold text-sm rounded hover:bg-indigo-700">
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default SuperadminTenants;
